import React, { useMemo, useRef, forwardRef, useImperativeHandle, memo, useState } from 'react';
import {  UseCanvas } from '@14islands/r3f-scroll-rig';
import HTMLText, {TEXT_VARIANTS} from '../../common/Text';
import useVisible3D from '../../../hooks/useVisible3D';
import dynamic from "next/dynamic";
import { useTextWithBreaksContext } from '../../common/TextWithBreaks';
import { gsap, useGSAP } from '@repo/utils/gsap';
import { usePageTransitionContext } from '../../PageTransition';
import { ANIMATION as TXT_ANIMATION } from '../../common/TextWithBreaks/utils';
import { getDelay } from '../../common/TextWithBreaks/TitleBreak';

const TrackText = dynamic(() => import('./TrackText'), {
	loading: () => {},
	ssr: false
});

const Reveal = dynamic(() => import('../../../../index').then((mod) => mod.Reveal), {
	ssr: false
});


const SourceText = forwardRef(
	function SourceText({
			className='',
			visible3D=false,
			variant='',
			color='',
			children,
			Tag,
			...props },
		fref)
	{
	return (
		<Tag
			ref={fref}
			className={`${className}  ${visible3D ? 'transparent-3d' : ''} ${variant} ${variant === TEXT_VARIANTS.ITALIC ? 'light' : ''}`}
			color={color}
			{...props}
		>
			{children}
		</Tag>
	)
})

const fontPath = '/fonts/Glober'
// eslint-disable-next-line no-redeclare
const Text = forwardRef(function Text({
	children,
	className = '',
	show3D = true,
	as: Tag = HTMLText,
	variant=TEXT_VARIANTS.NORMAL,
	webGLProps,
	color,
	hover=false,
  	uIndex=0,
	...props
}, fref){
	const TextRef = useRef();
	const visible3D = useVisible3D(show3D);
	const [revealTl] = useState(() => gsap.timeline({ paused: true }))
	const font = useMemo(() => {
		let fontType = 'GloberRegular'
		if (variant === TEXT_VARIANTS.ITALIC) fontType = 'GloberLightItalic'
		return `${fontPath}/${fontType}.woff`;
	}, [variant]);
	const { animation } = useTextWithBreaksContext()
	const { transitionCompleted } = usePageTransitionContext()
	useImperativeHandle(fref, () => TextRef.current, []);

	useGSAP(() => {
		if(!transitionCompleted || visible3D || animation !== TXT_ANIMATION.INIT_SHOW) return
		revealTl.play()
	},{ dependencies: [visible3D, transitionCompleted], revertOnUpdate: true })


	return (
		<>
			{(!visible3D && animation === TXT_ANIMATION.INIT_SHOW) ? (
				<Reveal
					paused
					timeline={revealTl}
					animationVars={{ delay: getDelay(uIndex) }}
				>
					<SourceText
						ref={TextRef}
						className={className}
						visible3D={visible3D}
						variant={variant}
						color={color}
						Tag={Tag}
						{...props}
					>
						{children}
					</SourceText>
				</Reveal>
			) : (
				<SourceText
					ref={TextRef}
					className={className}
					visible3D={visible3D}
					variant={variant}
					color={color}
					Tag={Tag}
					{...props}
				>
					{children}
				</SourceText>
			)}
			{visible3D && (
				<UseCanvas
					hover={hover}
					variant={variant}
					color={color}
					animation={animation}
					{...webGLProps}
				>
					<TrackText el={TextRef} font={font} uIndex={uIndex}  >
						{children}
					</TrackText>
				</UseCanvas>
			)}
		</>
	);
})

export default memo(Text);
